import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, SectionHead } from "./Reveal";
import { IMG, waLink } from "../lib/data";
import { useLang } from "../lib/i18n";

const spot = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
};

const Tag = ({ children, tone = "gold" }) => (
  <span className={`rounded-full border px-3 py-1 font-mono text-[9px] uppercase tracking-[0.18em] ${tone === "mint" ? "border-mint/40 text-mint bg-mint/5" : "border-gold/40 text-gold bg-gold/5"}`}>
    {children}
  </span>
);

export default function Services() {
  const { t } = useLang();
  const IMG_MAP = {
    "house-cleaning": IMG.house,
    "water-tank": IMG.tank,
    "ac-repair": IMG.ac,
    "pest-control": IMG.pest,
    "refrigerator-repair": IMG.fridge,
  };
  const CARD_SPANS = {
    "house-cleaning": "md:col-span-2 lg:col-span-8 lg:row-span-2 h-full",
    "water-tank": "lg:col-span-4",
    "ac-repair": "lg:col-span-4",
    "pest-control": "lg:col-span-4",
    "refrigerator-repair": "md:col-span-2 lg:col-span-8",
  };
  const HM = t.services.handyman;

  const Card = ({ item, minH }) => (
    <Link
      data-testid={`service-card-${item.id}`}
      to={`/book?service=${item.id}`}
      onMouseMove={spot}
      className={`group relative overflow-hidden rounded-3xl border border-white/10 bg-panel hover:border-gold/40 transition-colors duration-500 block h-full w-full ${minH}`}
    >
      <img
        src={IMG_MAP[item.id]}
        alt={item.title}
        className="absolute inset-0 w-full h-full object-cover opacity-55 group-hover:opacity-70 group-hover:scale-105 transition-all duration-[1200ms]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0E] via-[#0A0B0E]/55 to-transparent" />
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{ background: "radial-gradient(340px circle at var(--mx, 50%) var(--my, 50%), rgba(230,194,128,0.16), transparent 65%)" }}
      />
      <div className="relative h-full flex flex-col justify-end p-6 sm:p-8">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{item.idx}</span>
        <h3 className="font-display font-extrabold text-white text-xl sm:text-2xl lg:text-3xl mt-2 leading-tight">{item.title}</h3>
        <p className="text-slate-400 text-sm leading-relaxed mt-2 max-w-md">{item.desc}</p>
        <div className="flex flex-wrap items-center gap-2 mt-4">
          {item.tags.map(([label, tone]) => (
            <Tag key={label} tone={tone}>{label}</Tag>
          ))}
          <span className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-ink/60 backdrop-blur px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white group-hover:border-gold group-hover:text-gold transition-colors duration-300">
            {t.services.book} <ArrowUpRight size={13} />
          </span>
        </div>
      </div>
    </Link>
  );

  return (
    <section id="services" className="relative py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHead tag={t.services.tag} title={t.services.title} sub={t.services.sub} />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5 lg:gap-6 mt-14 lg:auto-rows-[290px]">
          {t.services.items.map((item, i) => (
            <Reveal key={item.id} className={`${CARD_SPANS[item.id]} min-h-[300px] lg:min-h-0`} delay={0.06 * (i % 3)}>
              <Card item={item} minH="" />
            </Reveal>
          ))}

          <Reveal className="lg:col-span-12" delay={0.16}>
            <a
              data-testid="service-card-handyman"
              href={waLink(`Hi OGO! I need a handyman visit. (${HM.title})`)}
              target="_blank"
              rel="noreferrer"
              className="group relative flex flex-col sm:flex-row sm:items-center gap-6 overflow-hidden rounded-3xl border border-gold/25 bg-gradient-to-r from-panel via-[#161a26] to-panel p-7 sm:p-8 hover:border-gold/60 transition-colors duration-500"
            >
              <div className="absolute inset-0 arch-grid opacity-60 pointer-events-none" />
              <div className="relative flex-1">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{HM.idx}</span>
                <h3 className="font-display font-extrabold text-white text-xl sm:text-2xl mt-2">{HM.title}</h3>
                <p className="text-slate-400 text-sm mt-2 max-w-2xl">{HM.desc}</p>
              </div>
              <span className="relative inline-flex items-center gap-2 rounded-full bg-gold text-ink font-semibold px-6 py-3 text-sm group-hover:bg-white transition-colors duration-300">
                {HM.cta} <ArrowUpRight size={15} />
              </span>
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
